import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { MobileModalStyle } from "../../../../../../common/constants/custom-style";
import Actions from "../../../../../../common/enums/Actions";
import { RemoveMapping } from "../../../../../../domain/models/remove-mapping";
import { CustomerDatabaseStore } from "../../../../../../store/main/CustomerDatabaseStore";
import Swal from "sweetalert2";
import { TerritoryDataStore } from "../../../../../../store/main/TerritoryDataStore";

interface Props {
  open: boolean;
  handleClose: Function;
  title: string;
  button: string;
  customer_id?: number;
  reasonOptions: string[];
  setOpenProgressBarModal: Function;
  setOpenFailedTransferModal: Function;
}

const BulkApprovalAndRejectCustomerModalMobile: React.FC<Props> = ({
  open,
  handleClose,
  title,
  button,
  reasonOptions,
  setOpenProgressBarModal,
  setOpenFailedTransferModal,
}) => {
  const {
    customerIds,
    mappingIds,
    deactivateCustomer,
    approveCustomer,
    fetchCustomerData,
    fetchCustomerSummaryCount,
    handleClearSelectedCustomers,
    setProgressCount,
    headquarterIds,
    setTitle,
    filterStore,
    clearFailedDeactivates,
  } = CustomerDatabaseStore();

  const { handleSubmit, control, setValue, watch } = useForm<RemoveMapping>({
    mode: "onChange",
  });

  const {
    headquarterMasterData,
    fetchDepartment,
    departmentData,
    customerTypesData,
  } = TerritoryDataStore();

  const style = {
    position: "absolute" as "absolute",
    top: "5%",
    right: "1%",
    cursor: "pointer",
    ":hover": { backgroundColor: "transparent" },
  };

  async function approveCustomerProgress(
    customer_ids: number[],
    mapping_ids: number[],
    reason: string,
    index = 0
  ) {
    let payload = {
      customer_ids: [customerIds[index]],
      mapping_ids: [mapping_ids[index]],
      reason: reason,
    };
    if (index < customer_ids.length) {
      setProgressCount(index);
      setTitle("Approve");
      setOpenProgressBarModal(true);

      await approveCustomer(payload);
      await approveCustomerProgress(
        customer_ids,
        mapping_ids,
        reason,
        index + 1
      );
    } else {
      Swal.fire({
        icon: "success",
        timer: 3000,
        text: "Customers Deactivated Successfully",
        customClass: {
          container: "swal2-style",
        },
        confirmButtonColor: "#3085d6",
      });
      handleClose(title);
      setOpenProgressBarModal(false);
    }
  }

  async function deactivateCustomerProgress(
    customer_ids: number[],
    reason: string,
    data: RemoveMapping,
    index = 0
  ) {
    const headquarterCode = headquarterMasterData.find(
      (item) => item.id.toString() === filterStore.headquarter_ids[0]
    );
    const customerSubName = customerTypesData.find(
      (item) => item.id === filterStore.customer_type_id
    );
    if (customer_ids.length === 1) {
      let payload = {
        customer_id: customerIds[index],
        mapping_id: mappingIds[index],
        team_id: filterStore.team_id,
        customer_sub_name: customerSubName?.sub_name,
        department_code: watch("department"),
        headquarter_code: headquarterCode?.ga_code
          ? headquarterCode?.ga_code
          : filterStore.headquarter_code,
        headquarter_id: filterStore.headquarter_ids[0]
          ? filterStore.headquarter_ids[0]
          : headquarterIds[0],
        reason: reason,
      };
      let result = await deactivateCustomer(payload, customer_ids[index], true);
      if (result.success) {
        Swal.fire({
          icon: "success",
          timer: 4000,
          title: "Customers Deactivated Successfully",
          customClass: {
            container: "swal2-style",
          },
          confirmButtonColor: "#3085d6",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Customer Deactivation Failed",
          text: result.errors.message ? result.errors.message : result.message,
          customClass: {
            container: "swal2-style",
          },
          confirmButtonColor: "#3085d6",
        });
      }
    } else {
      let payload = {
        customer_id: customerIds[index],
        mapping_id: mappingIds[index],
        team_id: filterStore.team_id,
        customer_sub_name: customerSubName?.sub_name,
        department_code: watch("department"),
        headquarter_code: headquarterCode?.ga_code,
        headquarter_id: filterStore.headquarter_ids[0]
          ? filterStore.headquarter_ids[0]
          : headquarterIds[0],
        reason: reason,
      };
      if (index < customer_ids.length) {
        setProgressCount(index);
        setTitle("Deactivate");
        setOpenProgressBarModal(true);
        await deactivateCustomer(payload, customer_ids[index], false);
        await deactivateCustomerProgress(customer_ids, reason, data, index + 1);
      } else {
        setOpenFailedTransferModal(true);
        setOpenProgressBarModal(false);
      }
    }
  }

  const RejectCustomer = (data: RemoveMapping) => {
    clearFailedDeactivates();
    deactivateCustomerProgress(
      customerIds,
      data.reasonSelect === "Other" ? data.reason : data.reasonSelect,
      data
    )
      .then(() => {
        handleClose(title);
        handleClearSelectedCustomers();
        fetchCustomerSummaryCount(filterStore);
        fetchCustomerData();
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          timer: 3000,
          text: "Unknown Error Occured! Try Again",
          customClass: {
            container: "swal2-style",
          },
          confirmButtonColor: "#3085d6",
        });
      });
  };

  const ApproveCustomer = (data: RemoveMapping) => {
    approveCustomerProgress(
      customerIds,
      mappingIds,
      data.reasonSelect === "Other" ? data.reason : data.reasonSelect
    )
      .then(() => {
        handleClose(title);
        handleClearSelectedCustomers();
        fetchCustomerSummaryCount(filterStore);
        fetchCustomerData();
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          timer: 3000,
          text: "Unknown Error Occured! Try Again",
          customClass: {
            container: "swal2-style",
          },
          confirmButtonColor: "#3085d6",
        });
      });
  };

  useEffect(() => {
    setValue("reason", "");
    setValue("reasonSelect", "");
    fetchDepartment();
  }, [open]);

  return (
    <>
      <Modal open={open}>
        <Box sx={MobileModalStyle}>
          <Stack gap={2}>
            <Typography
              textAlign="center"
              variant="h6"
              fontFamily="Segoe UI"
              fontWeight={600}
              color="#4D4D4D"
            >
              {title}
            </Typography>

            <Typography fontFamily="Segoe UI" fontWeight={600} color="#4D4D4D">
              Select Reason to {title}
            </Typography>
            <Button
              endIcon={<CloseRoundedIcon color="secondary" />}
              sx={style}
              onClick={() => handleClose(Actions.BULK_APPROVE_REJECT_CUSTOMER)}
            />
            <Controller
              name="department"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={value}
                    size="small"
                    label="Department"
                    onChange={onChange}
                  >
                    {departmentData?.map((dep) => (
                      <MenuItem value={dep.code}>{dep.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              rules={{
                required: { value: true, message: "Please Select Department" },
              }}
            />
            <Controller
              name="reasonSelect"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Reason</InputLabel>
                  <Select
                    value={value}
                    size="small"
                    label="Reason"
                    onChange={onChange}
                  >
                    {reasonOptions?.map((name) => (
                      <MenuItem value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              rules={{
                required: { value: true, message: "There must be a Reason" },
              }}
            />
            {watch("reasonSelect") === "Other" && (
              <Controller
                name="reason"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    fullWidth
                    value={value}
                    size="small"
                    onChange={onChange}
                    placeholder="Please type reason..."
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                rules={{
                  pattern: {
                    value: /^(?!.*[^a-zA-Z0-9 ]).{5,}$/,
                    message:
                      "Enter valid reason.  No special characters and minimum 5 characters",
                  },
                }}
              />
            )}

            <Stack direction="row" gap={3} justifyContent="center">
              <Button
                disabled={
                  !(
                    (watch("reasonSelect") &&
                      watch("reasonSelect") !== "Other") ||
                    (watch("reasonSelect") === "Other" &&
                      watch("reason") &&
                      watch("reason").length > 4)
                  )
                }
                variant="contained"
                color="success"
                sx={{
                  textTransform: "unset",
                  borderRadius: "30px",
                  height: "40px",
                  minWidth: "30%",
                }}
                onClick={handleSubmit(ApproveCustomer)}
              >
                <Typography
                  variant="subtitle2"
                  fontFamily="Segoe UI"
                  color="white"
                >
                  Approve
                </Typography>
              </Button>
              <Button
                disabled={
                  !(
                    (watch("reasonSelect") &&
                      watch("reasonSelect") !== "Other") ||
                    (watch("reasonSelect") === "Other" &&
                      watch("reason") &&
                      watch("reason").length > 4)
                  )
                }
                variant="contained"
                color="error"
                sx={{
                  textTransform: "unset",
                  borderRadius: "30px",
                  height: "40px",
                  bgcolor: "#EF5466",
                  minWidth: "30%",
                }}
                onClick={handleSubmit(RejectCustomer)}
              >
                <Typography
                  variant="subtitle2"
                  fontFamily="Segoe UI"
                  color="white"
                >
                  Reject
                </Typography>
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default BulkApprovalAndRejectCustomerModalMobile;
